import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  FluentProvider,
  Spinner,
  Text,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeft24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { formatDate, isWorkOfferPending, type IWorkOffer } from '@moko/utils';
import { useActiveTheme } from '../theme/ThemeContext';
import useFetch from '../hooks/useFetch';
import type { IWorkOfferServices } from './types';

type ResponseAction = 'accept' | 'reject';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase500,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalM,
    columnGap: tokens.spacingHorizontalXXL,
    maxWidth: '720px',
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    color: tokens.colorNeutralForeground1,
  },
  dialogNote: {
    minWidth: '320px',
  },
});

const formatPrice = (value?: number | null): string =>
  value === undefined || value === null ? '-' : String(value);

const displayValue = (value?: string | number | null): string => {
  if (value === undefined || value === null || value === '') return '-';
  return String(value);
};

interface InfoRowProps {
  label: string;
  value: string;
}

const InfoRow = ({ label, value }: InfoRowProps) => {
  const styles = useStyles();
  return (
    <div className={styles.fieldRow}>
      <Text size={200} className={styles.label}>
        {label}
      </Text>
      <Text weight="semibold" className={styles.value}>
        {value}
      </Text>
    </div>
  );
};

export interface WorkOfferDetailsProps {
  workOfferId: number;
  services: Pick<
    IWorkOfferServices,
    'getWorkOffer' | 'acceptWorkOffer' | 'rejectWorkOffer'
  >;
  onBack: () => void;
  setIsInProgress: (value: boolean) => void;
  setErrorMessage: (message: string) => void;
  setSuccessMessage?: (message: string) => void;
  readOnly?: boolean;
}

const WorkOfferDetails = ({
  workOfferId,
  services,
  onBack,
  setIsInProgress,
  setErrorMessage,
  setSuccessMessage,
  readOnly = false,
}: WorkOfferDetailsProps) => {
  const { t } = useTranslation();
  const theme = useActiveTheme();
  const styles = useStyles();

  const [dialogAction, setDialogAction] = useState<ResponseAction | null>(null);
  const [responseNote, setResponseNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const idValid = Number.isFinite(workOfferId) && workOfferId > 0;

  const {
    data: offer,
    loading,
    refetch,
  } = useFetch<IWorkOffer>({
    queryKey: ['workOffer', workOfferId],
    enabled: idValid,
    queryFn: (abortController) =>
      services.getWorkOffer(abortController, workOfferId),
    onLoading: () => setIsInProgress(true),
    onLoaded: () => setIsInProgress(false),
    onError: (error) =>
      setErrorMessage(
        error.message ||
          t('failedToLoadWorkOffer', 'Failed to load work offer.')
      ),
  });

  useEffect(() => {
    if (!idValid) {
      setErrorMessage(t('invalidWorkOfferId', 'Invalid work offer id.'));
    }
  }, [idValid, setErrorMessage, t]);

  const pending = isWorkOfferPending(offer?.status);

  const closeDialog = () => {
    if (submitting) return;
    setDialogAction(null);
    setResponseNote('');
  };

  const handleConfirm = async () => {
    if (!offer || !dialogAction) return;
    setSubmitting(true);
    setIsInProgress(true);
    const abortController = new AbortController();
    const note = responseNote.trim() || undefined;
    try {
      if (dialogAction === 'accept') {
        const response = await services.acceptWorkOffer(
          abortController,
          offer.id,
          { responseNote: note }
        );
        if (!response.isSuccess) {
          setErrorMessage(
            response.message ??
              t(
                'failedToRespondToWorkOffer',
                'Failed to respond to work offer.'
              )
          );
          return;
        }
        if (response.workAssignmentId == null) {
          setSuccessMessage?.(
            response.message ??
              t(
                'workOfferAcceptedNotAssigned',
                'Offer accepted, but no work assignment was created.'
              )
          );
        } else {
          setSuccessMessage?.(
            `${t(
              'workOfferAcceptedAssigned',
              'Offer accepted. Work assignment id:'
            )} ${response.workAssignmentId}`
          );
        }
      } else {
        const response = await services.rejectWorkOffer(
          abortController,
          offer.id,
          { responseNote: note }
        );
        if (!response.isSuccess) {
          setErrorMessage(
            response.message ??
              t(
                'failedToRespondToWorkOffer',
                'Failed to respond to work offer.'
              )
          );
          return;
        }
        setSuccessMessage?.(
          response.message ?? t('workOfferRejected', 'Work offer rejected.')
        );
      }
      setDialogAction(null);
      setResponseNote('');
      refetch();
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : t('failedToRespondToWorkOffer', 'Failed to respond to work offer.');
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
      setIsInProgress(false);
    }
  };

  const dialogTitle =
    dialogAction === 'accept'
      ? t('acceptWorkOffer', 'Accept work offer')
      : t('rejectWorkOffer', 'Reject work offer');

  const confirmLabel =
    dialogAction === 'accept' ? t('accept', 'Accept') : t('reject', 'Reject');

  const fields = useMemo(() => {
    if (!offer) return [];
    return [
      { label: t('id', 'Id'), value: displayValue(offer.id) },
      {
        label: t('workOfferBatchId', 'Batch Id'),
        value: displayValue(offer.workOfferBatchId),
      },
      { label: t('workId', 'Work Id'), value: displayValue(offer.workId) },
      {
        label: t('contractor', 'Contractor'),
        value: displayValue(offer.contractorName ?? offer.contractorId),
      },
      { label: t('status', 'Status'), value: displayValue(offer.status) },
      {
        label: t('offeredPrice', 'Offered Price'),
        value: formatPrice(offer.finalOfferedPrice),
      },
      {
        label: t('agreedPrice', 'Agreed Price'),
        value: formatPrice(offer.agreedPrice),
      },
      {
        label: t('responseNote', 'Response Note'),
        value: displayValue(offer.responseNote),
      },
      {
        label: t('respondedAt', 'Responded At'),
        value: offer.respondedAt ? formatDate(offer.respondedAt, true) : '-',
      },
      {
        label: t('workAssignmentId', 'Work Assignment Id'),
        value: displayValue(offer.workAssignmentId),
      },
      {
        label: t('createdAt', 'Created at'),
        value: offer.createdAt ? formatDate(offer.createdAt, true) : '-',
      },
    ];
  }, [offer, t]);

  return (
    <FluentProvider theme={theme} style={{ background: 'transparent' }}>
      <div className={styles.root}>
        <div className={styles.header}>
          <Button
            shape="circular"
            appearance="primary"
            icon={<ArrowLeft24Regular />}
            onClick={onBack}
            aria-label={t('back', 'Back')}
          />
          <div className={styles.title}>
            {`${t('workOffer', 'Work Offer')} #${workOfferId}`}
          </div>
        </div>

        {offer && !readOnly && (
          <div
            className={styles.actions}
            aria-label={t('workOfferActions', 'Work offer actions')}
          >
            <Button
              appearance="primary"
              icon={<Checkmark24Regular />}
              disabled={!pending || submitting}
              onClick={() => setDialogAction('accept')}
            >
              {t('accept', 'Accept')}
            </Button>
            <Button
              appearance="outline"
              icon={<Dismiss24Regular />}
              disabled={!pending || submitting}
              onClick={() => setDialogAction('reject')}
            >
              {t('reject', 'Reject')}
            </Button>
          </div>
        )}

        {loading && <Spinner label={t('loading', 'Loading...')} />}
        {!loading && !offer && (
          <Text>{t('workOfferNotFound', 'Work offer not found.')}</Text>
        )}
        {offer && (
          <div className={styles.fields}>
            {fields.map((field) => (
              <InfoRow
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </div>
        )}

        {offer && (
          <Dialog
            open={dialogAction !== null}
            onOpenChange={(_, data) => {
              if (!data.open) closeDialog();
            }}
          >
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent className={styles.dialogNote}>
                  <Field
                    label={t('responseNote', 'Response Note')}
                    hint={t('optional', 'Optional')}
                  >
                    <Textarea
                      value={responseNote}
                      onChange={(_, data) => setResponseNote(data.value)}
                      rows={4}
                      disabled={submitting}
                    />
                  </Field>
                </DialogContent>
                <DialogActions>
                  <Button
                    appearance="secondary"
                    onClick={closeDialog}
                    disabled={submitting}
                  >
                    {t('cancel', 'Cancel')}
                  </Button>
                  <Button
                    appearance="primary"
                    onClick={handleConfirm}
                    disabled={submitting}
                    icon={submitting ? <Spinner size="tiny" /> : undefined}
                  >
                    {confirmLabel}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        )}
      </div>
    </FluentProvider>
  );
};

export default WorkOfferDetails;
